// The text a TextBox keeps: plain lines, with three marks the shortcuts make.
// A bullet is "- " (two spaces of indent per level), bold is **text**, and a
// highlight is ==text==. Kept as text rather than html so the Room, the model,
// and the clipboard all read it as written. No DOM in here except `htmlToText`,
// which walks html the prep sanitizer has already reduced to ul, li, b, mark,
// p, and br.

import { escapeHtml } from "./prepEdit";

/** One line of kept text as safe inline html, for showing it outside a box. */
export function inline(text: string): string {
  return escapeHtml(text)
    .replace(/\*\*(.+?)\*\*/g, "<b>$1</b>")
    .replace(/==(.+?)==/g, "<mark>$1</mark>");
}

const BULLET = /^( *)- (.*)$/;

/** The kept text as editor html (the sanitizer's subset). */
export function textToHtml(text: string): string {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const out: string[] = [];
  let depth = -1;
  const closeTo = (target: number): void => {
    while (depth > target) {
      out.push("</li></ul>");
      depth -= 1;
    }
  };
  for (const line of lines) {
    const m = BULLET.exec(line);
    if (!m) {
      closeTo(-1);
      out.push(line.trim() === "" ? "<p><br></p>" : `<p>${inline(line)}</p>`);
      continue;
    }
    const want = Math.min(Math.floor((m[1] ?? "").length / 2), depth + 1);
    if (want > depth) {
      while (depth < want) {
        out.push("<ul><li>");
        depth += 1;
      }
    } else {
      closeTo(want);
      out.push("</li><li>");
    }
    out.push(m[2] ? inline(m[2]) : "<br>");
  }
  closeTo(-1);
  return out.join("");
}

function inlineText(node: Node): string {
  let s = "";
  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) s += (child.textContent ?? "").replace(/\n/g, " ");
    else if (child instanceof HTMLElement) {
      const tag = child.tagName.toLowerCase();
      if (tag === "ul") return;
      const inner = inlineText(child);
      if (tag === "b") s += inner.trim() ? `**${inner}**` : inner;
      else if (tag === "mark") s += inner.trim() ? `==${inner}==` : inner;
      else if (tag !== "br") s += inner;
    }
  });
  return s;
}

function listLines(ul: Element, depth: number, out: string[]): void {
  for (const li of Array.from(ul.children)) {
    if (li.tagName.toLowerCase() !== "li") continue;
    out.push(`${"  ".repeat(depth)}- ${inlineText(li).trim()}`);
    for (const sub of Array.from(li.children)) {
      if (sub.tagName.toLowerCase() === "ul") listLines(sub, depth + 1, out);
    }
  }
}

/** Sanitized editor html back to the kept text. */
export function htmlToText(html: string): string {
  const root = document.createElement("div");
  root.innerHTML = html;
  const out: string[] = [];
  root.childNodes.forEach((node) => {
    if (node instanceof HTMLElement) {
      const tag = node.tagName.toLowerCase();
      if (tag === "ul") listLines(node, 0, out);
      else if (tag === "br") out.push("");
      else out.push(inlineText(node));
    } else if (node.nodeType === Node.TEXT_NODE && (node.textContent ?? "").trim()) {
      out.push(node.textContent ?? "");
    }
  });
  while (out.length > 0 && out[out.length - 1]?.trim() === "") out.pop();
  return out.join("\n");
}
